import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Search, MessageCircle, Plus, CreditCard, LogOut, User, ShoppingBag, LogIn, HelpCircle, Calendar } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { ShopMessageThread } from "@/components/ShopMessageThread";
import { PaymentMethodSelector } from "@/components/PaymentMethodSelector";
import { useNavigate, Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CookieBanner } from "@/components/CookieBanner";

export default function Marketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [items, setItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [showMessages, setShowMessages] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [shopSettings, setShopSettings] = useState<any>(null);
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);
  const [isParent, setIsParent] = useState(false);
  const { balance } = useParentCredits(ptaId || undefined);

  useEffect(() => {
    fetchPtaId();
  }, [user]);

  useEffect(() => {
    if (ptaId) {
      fetchPTAData();
      fetchCategories();
      fetchItems();
      fetchShopSettings();
    }
  }, [ptaId]);

  useEffect(() => {
    if (user && ptaId) {
      checkIfParent();
    }
  }, [user, ptaId]);

  // Redirect if marketplace is disabled
  useEffect(() => {
    if (ptaData && ptaData.show_marketplace_section === false) {
      toast({
        title: "Marketplace Unavailable",
        description: "The marketplace is not enabled for this PTA.",
        variant: "destructive"
      });
      navigate(`/fundraising/${ptaId}`);
    }
  }, [ptaData, navigate, ptaId]);

  const checkIfParent = async () => {
    if (!user || !ptaId) return;

    const { data } = await supabase
      .from('parents')
      .select('id')
      .eq('user_id', user.id)
      .eq('pta_id', ptaId)
      .single();

    setIsParent(!!data);
  };

  const fetchPTAData = async () => {
    if (!ptaId) return;
    
    // Use RPC function to bypass RLS for anonymous users
    const { data } = await supabase
      .rpc('get_public_pta_info', { p_pta_id: ptaId })
      .single();
    
    if (data) {
      setPtaData(data);
    }
  };

  const fetchShopSettings = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('shop_settings')
      .select('*')
      .eq('pta_id', ptaId)
      .single();
    
    if (data) setShopSettings(data);
  };

  const fetchPtaId = async () => {
    // Check URL query parameter first
    const urlParams = new URLSearchParams(window.location.search);
    const ptaParam = urlParams.get('pta');
    
    if (ptaParam) {
      setPtaId(ptaParam);
      return;
    }
    
    if (!user) return;
    
    const { data: memberData } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user.id)
      .single();

    if (memberData) {
      setPtaId(memberData.pta_id);
    } else {
      const { data: parentData } = await supabase
        .from('parents')
        .select('pta_id')
        .eq('user_id', user.id)
        .single();
      
      if (parentData) {
        setPtaId(parentData.pta_id);
      }
    }
  };

  const fetchCategories = async () => {
    if (!ptaId) return;
    
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('pta_id', ptaId)
      .order('display_order');
    
    setCategories(data || []);
  };

  const fetchItems = async () => {
    if (!ptaId) return;
    
    setLoading(true);
    const { data, error } = await supabase
      .from('shop_items')
      .select(`
        *,
        seller_parent:parents!seller_parent_id(full_name),
        pta:ptas(name)
      `)
      .eq('pta_id', ptaId)
      .eq('status', 'active')
      .eq('approval_status', 'approved')
      .or(`expires_at.is.null,expires_at.gt.${new Date().toISOString()}`)
      .order('is_featured', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });

    if (error) {
      toast({
        title: "Error loading items",
        description: error.message,
        variant: "destructive"
      });
    } else {
      setItems(data || []);
    }
    setLoading(false);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (item.description?.toLowerCase().includes(searchQuery.toLowerCase()) || false);
    const matchesCategory = categoryFilter === "all" || item.category === categoryFilter;
    const matchesSeller = sellerFilter === "all" || item.seller_type === sellerFilter;
    return matchesSearch && matchesCategory && matchesSeller;
  });

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(`/fundraising/${ptaId}`);
  };

  const logoUrl = ptaData?.logo_url;

  return (
    <div className="min-h-screen bg-background">
      {/* Header matching FundraisingPage */}
      <header 
        className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
        style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
      >
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-8">
            {/* Logo - only show name if no logo exists */}
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <>
                  <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                    <span className="text-lg">🏫</span>
                  </div>
                  <span className="font-semibold text-lg hidden sm:inline-block">
                    {ptaData?.name || 'PTA Marketplace'}
                  </span>
                </>
              )}
            </Link>

            {/* Centered Navigation */}
            <nav className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to={`/fundraising/${ptaId}`} className="text-sm font-medium hover:text-primary transition-colors">
                Home
              </Link>
              <button onClick={() => {
                navigate(`/fundraising/${ptaId}`);
                setTimeout(() => {
                  const element = document.getElementById('events');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }} className="text-sm font-medium hover:text-primary transition-colors">
                Events
              </button>
              <button onClick={() => {
                navigate(`/fundraising/${ptaId}`);
                setTimeout(() => {
                  const element = document.getElementById('goals');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }} className="text-sm font-medium hover:text-primary transition-colors">
                Goals
              </button>
              <Link to={`/marketplace?pta=${ptaId}`} className="text-sm font-medium text-primary">
                Marketplace
              </Link>
              <button onClick={() => {
                navigate(`/fundraising/${ptaId}`);
                setTimeout(() => {
                  const element = document.getElementById('about');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }} className="text-sm font-medium hover:text-primary transition-colors">
                About
              </button>
            </nav>
          </div>

          {/* User Actions */}
          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/parent-profile`)}>
                    <User className="mr-2 h-4 w-4" />
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container py-8">
        <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Marketplace</h1>
          <p className="text-muted-foreground mt-2">Buy and sell items within your PTA community</p>
        </div>
        
        {user && (
          <div className="flex gap-2 items-center">
            <Dialog open={howItWorksOpen} onOpenChange={setHowItWorksOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <HelpCircle className="h-4 w-4 mr-2" />
                  How it Works
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>How the Marketplace Works</DialogTitle>
                  <DialogDescription>
                    Everything you need to know about buying and selling in the PTA marketplace
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">For Buyers</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                      <li>Browse items listed by other parents and the PTA</li>
                      <li>Use the search and filters to find what you need</li>
                      <li>Message sellers directly about items</li>
                      <li>Arrange collection or payment directly with sellers</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-semibold mb-2">For Sellers</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                      <li>Purchase credits to list items (£{shopSettings?.credit_cost_per_post || 1} credit per listing)</li>
                      <li>Upload up to 3 photos of your item</li>
                      <li>Set your price or mark as free/negotiable</li>
                      <li>Receive messages from interested buyers</li>
                      {shopSettings?.post_expiry_days && (
                        <li>Listings expire after {shopSettings.post_expiry_days} days</li>
                      )}
                      {shopSettings?.parent_post_approval_required && (
                        <li>All listings require PTA approval before going live</li>
                      )}
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-semibold mb-2">Credits System</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                      <li>Credits are required to list items for sale</li>
                      <li>Purchase credits through the "Buy Credits" button</li>
                      <li>Credits are specific to your PTA</li>
                      <li>Your credit balance is shown in the top right</li>
                    </ul>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="font-semibold mb-2">Important Notes</h4>
                    <ul className="space-y-1 text-sm text-muted-foreground list-disc list-inside">
                      <li>The PTA is not responsible for transactions between parents</li>
                      <li>All payments and collections are arranged directly between buyer and seller</li>
                      <li>Please review the terms of use before listing items</li>
                      <li>Report any inappropriate listings to the PTA</li>
                    </ul>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {balance && (
              <Card className="px-4 py-2">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-primary" />
                  <div className="text-sm">
                    <div className="font-semibold">{balance.credits_available} Credits</div>
                    {balance.credits_pending > 0 && (
                      <div className="text-xs text-muted-foreground">
                        {balance.credits_pending} pending
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            )}
            <Button onClick={() => navigate('/marketplace/buy-credits')} variant="outline" size="sm">
              Buy Credits
            </Button>
            {isParent && (
              <>
                <Button onClick={() => navigate('/marketplace/my-listings')} variant="outline" size="sm">
                  My Listings
                </Button>
                <Button 
                  onClick={() => navigate('/marketplace/create')} 
                  size="sm"
                  disabled={!balance || balance.credits_available < 1}
                  title={!balance || balance.credits_available < 1 ? "You need credits to create a listing" : ""}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Sell Item
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[300px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search items..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sellerFilter} onValueChange={setSellerFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Seller Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sellers</SelectItem>
            <SelectItem value="pta">PTA Items</SelectItem>
            <SelectItem value="parent">Parent Items</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading items...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No items found</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          {filteredItems.map((item) => (
            <Card 
              key={item.id} 
              className="cursor-pointer hover:shadow-lg transition-shadow relative overflow-hidden"
              onClick={() => setSelectedItem(item)}
            >
              {/* Featured ribbon (top-right corner) */}
              {item.is_featured && (
                <div className="absolute top-0 right-0 z-10 overflow-hidden w-20 h-20">
                  <div className="absolute bg-destructive text-destructive-foreground text-[10px] font-bold py-1 text-center transform rotate-45 origin-center w-28 -right-7 top-4 shadow-md">
                    Featured
                  </div>
                </div>
              )}
              {/* Sale/New ribbon (top-left corner) */}
              {item.ribbon && (
                <div className="absolute top-0 left-0 z-10 overflow-hidden w-20 h-20">
                  <div className={`absolute text-white text-[10px] font-bold py-1 text-center transform -rotate-45 origin-center w-28 -left-7 top-4 shadow-md ${
                    item.ribbon === 'sale' ? 'bg-red-600' : 'bg-green-600'
                  }`}>
                    {item.ribbon === 'sale' ? 'Sale' : 'New'}
                  </div>
                </div>
              )}
              <CardContent className="p-4">
                <div className="aspect-square relative mb-3 bg-muted rounded-lg overflow-hidden">
                  {item.images && item.images.length > 0 ? (
                    <img 
                      src={item.images[0]} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-6xl">
                      📦
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-sm mb-1 line-clamp-2">{item.name}</h3>
                {item.price && (
                  <p className="text-lg font-bold text-primary mb-1">£{parseFloat(item.price).toFixed(2)}</p>
                )}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge variant={item.seller_type === 'pta' ? 'default' : 'secondary'} className="text-xs">
                    {item.seller_type === 'pta' ? 'PTA' : item.seller_parent?.full_name}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{item.category}</Badge>
                  {item.condition && (
                    <Badge variant="outline" className="text-xs capitalize">{item.condition.replace('_', ' ')}</Badge>
                  )}
                  {item.garment_size && (
                    <Badge variant="outline" className="text-xs">Size: {item.garment_size}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Listed {new Date(item.created_at).toLocaleDateString()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {selectedItem && (
        <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedItem.name}</DialogTitle>
              <DialogDescription>
                <div className="flex gap-2 mt-2">
                  <Badge variant={selectedItem.seller_type === 'pta' ? 'default' : 'secondary'}>
                    {selectedItem.seller_type === 'pta' ? `PTA - ${selectedItem.pta?.name}` : `Parent - ${selectedItem.seller_parent?.full_name}`}
                  </Badge>
                  <Badge variant="outline">{selectedItem.category}</Badge>
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {selectedItem.images && selectedItem.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedItem.images.map((img: string, idx: number) => (
                    <img 
                      key={idx}
                      src={img} 
                      alt={`${selectedItem.name} ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                  ))}
                </div>
              )}
              
              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {selectedItem.description || 'No description provided'}
                </p>
              </div>

              {selectedItem.price && (
                <div>
                  <h4 className="font-semibold mb-2">Price</h4>
                  <p className="text-2xl font-bold text-primary">£{parseFloat(selectedItem.price).toFixed(2)}</p>
                </div>
              )}

              {selectedItem.condition && (
                <div>
                  <h4 className="font-semibold mb-2">Condition</h4>
                  <Badge variant="secondary" className="capitalize">{selectedItem.condition.replace('_', ' ')}</Badge>
                </div>
              )}

              {selectedItem.garment_size && (
                <div>
                  <h4 className="font-semibold mb-2">Size</h4>
                  <Badge variant="secondary">{selectedItem.garment_size}</Badge>
                </div>
              )}

              {selectedItem.contact_method && (
                <div>
                  <h4 className="font-semibold mb-2">Contact Details</h4>
                  <p className="text-sm">{selectedItem.contact_method}</p>
                </div>
              )}

              {user && (
                <div className="space-y-2">
                  {selectedItem.seller_type === 'pta' && selectedItem.price && (
                    <Button 
                      className="w-full"
                      onClick={() => setShowPaymentSelector(true)}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay £{parseFloat(selectedItem.price).toFixed(2)}
                    </Button>
                  )}
                  <Button 
                    className="w-full"
                    variant={selectedItem.seller_type === 'pta' ? 'outline' : 'default'}
                    onClick={() => setShowMessages(true)}
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Message Seller
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {showMessages && selectedItem && user && (
        <ShopMessageThread
          itemId={selectedItem.id}
          itemName={selectedItem.name}
          open={showMessages}
          onOpenChange={setShowMessages}
        />
      )}

      {showPaymentSelector && selectedItem && shopSettings && (
        <PaymentMethodSelector
          open={showPaymentSelector}
          onOpenChange={setShowPaymentSelector}
          shopSettings={shopSettings}
          itemName={selectedItem.name}
          itemPrice={selectedItem.price ? parseFloat(selectedItem.price) : 0}
          onPaymentMethodSelected={(method) => {
            console.log("Selected payment method:", method);
            // TODO: Handle payment flow when Stripe is integrated
            toast({
              title: "Payment Method Selected",
              description: `${method} payment will be processed when Stripe is integrated.`
            });
          }}
        />
      )}
        </div>
      </div>

      {/* Footer matching FundraisingPage */}
      <footer className="bg-muted/30 border-t border-border mt-16">
        <div className="container py-12">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={`/fundraising/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to={`/marketplace?pta=${ptaId}`} className="text-primary font-medium">
                    Marketplace
                  </Link>
                </li>
              </ul>
            </div>

            {/* Get Involved */}
            <div>
              <h4 className="font-semibold mb-3">Get Involved</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={`/fundraising/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Donate
                  </Link>
                </li>
                <li>
                  <Link to={`/become-a-member/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Become a Member
                  </Link>
                </li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-semibold mb-3">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/refund" className="text-muted-foreground hover:text-primary transition-colors">
                    Refund Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {ptaData?.shared_email && <li>{ptaData.shared_email}</li>}
                {ptaData?.charity_number && <li>Charity: {ptaData.charity_number}</li>}
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-foreground">
            <p>© {new Date().getFullYear()} The PTA Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
